import { Avatar, Pastille } from '@taki/ui';
import { useEffect, useRef, useState, type JSX, type ReactNode } from 'react';
import {
  IconBellNotification, IconChatTwoBubblesOval, IconChevronRight, IconFullSpaced, IconPadlockSquare1, IconPlus, IconSignHashtag,
  IconPen1, IconUserFaceMale, aUnBrouillon, cleDeConversation, cleDeSujetLia, cn, ordreDesEpinglees, useClesDeBrouillons,
} from '@taki/ui';
import { LiaHead } from '../surfaces/LiaLauncher.js';
import { IconeEpingle } from './IconeEpingle.js';
import { IconeDeFiche } from './IconeDeFiche.js';
import { figureDansLesEpinglees } from './sourcesConversations.js';
import { badgeDeSource, groupesDuRail, SOURCES_DU_CENTRE, type ConversationResume, type SourceDeConversation, type SourceId } from './sources.js';

/**
 * LE RAIL DU PANNEAU COMPACT (maquettes hub-d et hub-e2) : une colonne
 * d'icones de 2,75rem, une par source (Notifications, Ici, Lia, Canaux,
 * Messages directs), avec leurs non lus ; et, depliee, la LISTE des
 * conversations a cote, qui elargit le panneau vers la gauche. Fenetre large,
 * la liste est TOUJOURS depliee et se redimensionne par sa bordure droite
 * (retouches du 06/10/2026) ; le rail d'icones ne sert plus que sous 1100 px,
 * ou la liste se superpose au fil.
 *
 * Le centre agrandi, lui, ne passe pas par ici : son rail est celui du
 * gabarit (`ScreenLayout`, `menuItems` groupes).
 */
export interface RailDuCentreProps {
  source: SourceId;
  onSource: (source: SourceId) => void;
  conversations: readonly ConversationResume[];
  conversationActive: string | null;
  onConversation: (source: SourceId, id: string) => void;
  onAjouter: (source: SourceId) => void;
  deplie: boolean;
  onDeplier: (deplie: boolean) => void;
  /** La largeur choisie de la liste depliee, en px (bordure glissee) ; absente : 15rem. */
  largeur?: number | undefined;
  /** R12 : epingler ou desepingler (icone au survol, menu de la ligne). */
  onSourdine?: (conversation: ConversationResume) => void;
  onReglagesExternes?: (conversation: ConversationResume) => void;
  sourdines?: ReadonlySet<string>;
  onEpingler?: (conversation: ConversationResume) => void;
  /** R4 : renommer en place (double-clic, menu), si la ligne est `renommable`. */
  onRenommer?: (conversation: ConversationResume, titre: string) => void | Promise<void>;
  /** R4 : supprimer (archiver) apres confirmation, si la ligne est `supprimable`. */
  onSupprimer?: (conversation: ConversationResume) => void;
  /** Les refs epinglees dans l'ordre d'epinglage (`preferences.observerEpinglees`), pour trier le groupe. */
  ordreEpingles?: readonly string[];
  /** Les sources pretes mais sans port : rendues inertes, avec leur raison (G16). */
  raisonSansPort?: string | null;
}

export function IconeDeSource({ id, size = 16 }: { id: SourceId; size?: number }): JSX.Element {
  if (id === 'notifications') return <IconBellNotification size={size} />;
  if (id === 'ici') return <IconeDeFiche size={size} />;
  if (id === 'lia') return <LiaHead size={size} />;
  if (id === 'canaux') return <IconSignHashtag size={size} />;
  return <IconUserFaceMale size={size} />;
}

function Badge({ nombre, point }: { nombre: number; point: boolean }): JSX.Element | null {
  if (nombre > 0) return <span aria-label={`${nombre} non lus`} className="absolute -right-0.5 -top-0.5 min-w-[0.875rem] rounded-pill bg-ink-900 px-[0.1875rem] text-center font-sans text-[0.5625rem] font-semibold leading-[0.875rem] text-surface">{nombre}</span>;
  if (point) return <span aria-label="Réponse prête" className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-pill bg-accent-dot" />;
  return null;
}

/**
 * Les sources que le rail d'icones montre. « Ici » n'y est jamais (R11 du
 * soir) : le suivi d'une fiche s'atteint par le volet de la fiche, ou par la
 * liste quand il est epingle (ou porte des non lus).
 */
export function sourcesVisibles(): readonly SourceDeConversation[] {
  return SOURCES_DU_CENTRE.filter((source) => source.id !== 'ici');
}

export function RailDuCentre(props: RailDuCentreProps): JSX.Element {
  const { source, onSource, conversations, deplie, onDeplier } = props;
  // Depliee (hub-e2), la liste REMPLACE le rail d'icones : la fenetre s'elargit d'autant vers la gauche.
  if (deplie) return <ListeDuRail {...props} />;
  return <>
    <nav aria-label="Sources du centre" className="flex w-11 shrink-0 flex-col items-center gap-1.5 bg-surface-1 pb-2 pt-3">
      {sourcesVisibles().map((entree) => {
        const badge = badgeDeSource(conversations, entree.id);
        const active = entree.id === source;
        return <button key={entree.id} type="button" onClick={() => onSource(entree.id)} aria-pressed={active}
          aria-label={entree.libelle}
          title={entree.libelle}
          className={cn('relative flex h-8 w-8 items-center justify-center rounded-sm text-ink-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
            active ? 'bg-surface-3 text-ink-900' : 'hover:bg-surface-3 hover:text-ink-900')}>
          <IconeDeSource id={entree.id} />
          <Badge nombre={badge.nombre} point={badge.point} />
        </button>;
      })}
      <span className="flex-1" />
      {/* La fleche de hub-e1 : un carre cerne d'accent, bien visible, avec son info-bulle et son raccourci. */}
      <FlecheDeplier onDeplier={() => onDeplier(true)} />
    </nav>
  </>;
}

/**
 * La fleche du rail ferme (hub-e1). Son info-bulle se pose A GAUCHE de la
 * fenetre, hors de son bord : elle est donc rendue en `fixed`, au rectangle du
 * bouton, sinon le bord arrondi de la fenetre (overflow hidden) la couperait.
 */
function FlecheDeplier({ onDeplier }: { onDeplier: () => void }): JSX.Element {
  const [bulle, setBulle] = useState<{ right: number; bottom: number } | null>(null);
  const montrer = (element: HTMLElement): void => {
    const r = element.getBoundingClientRect();
    setBulle({ right: window.innerWidth - r.left + 8, bottom: window.innerHeight - r.bottom });
  };
  return <>
    <button type="button" onClick={onDeplier} aria-expanded={false} aria-label="Déplier la liste" aria-keyshortcuts="Control+Shift+L"
      aria-describedby={bulle ? 'taki-centre-bulle-deplier' : undefined}
      onPointerEnter={(event) => montrer(event.currentTarget)} onPointerLeave={() => setBulle(null)}
      onFocus={(event) => montrer(event.currentTarget)} onBlur={() => setBulle(null)}
      className="flex h-8 w-8 items-center justify-center rounded-md border border-accent-border bg-surface text-ink-700 shadow-card hover:bg-accent-tint hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border">
      <IconChevronRight size={14} />
    </button>
    {bulle && <span id="taki-centre-bulle-deplier" role="tooltip" style={{ right: bulle.right, bottom: bulle.bottom }}
      className="pointer-events-none fixed z-50 flex h-8 items-center whitespace-nowrap rounded-sm border border-border bg-surface px-2 text-[0.8125rem] font-medium text-ink-900 shadow-pop">
      Déplier la liste<kbd className="ml-1.5 font-mono text-[0.6875rem] text-ink-500">Ctrl Maj L</kbd>
    </span>}
  </>;
}

/**
 * LA LISTE DEPLIEE (hub-e2) : le rail de portes du gabarit en petit, 15rem de
 * large par defaut, surface-1, groupes avec en-tete et « + », Notifications en
 * tete. Rien en pied (retouches du 06/10/2026 : « Replier la liste » retire,
 * la liste se redimensionne par sa bordure ; R6 et R11 du soir : ni
 * « Archivees », qui se consultent dans l'app Canaux, ni « Ici » : un suivi de
 * fiche n'a pas de groupe, il vit dans « Epinglees »).
 */
export function ListeDuRail({ source, conversations, conversationActive, onConversation, onSource, onAjouter, largeur, raisonSansPort = null, onEpingler, onRenommer, onSupprimer, onSourdine, onReglagesExternes, sourdines = new Set(), ordreEpingles = [] }: RailDuCentreProps): JSX.Element {
  const ligne = (active: boolean) => cn('flex h-8 w-full items-center gap-2 rounded-[0.5rem] px-2 text-left text-[0.8125rem] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
    active ? 'bg-surface-3 font-semibold text-ink-900' : 'text-ink-700 hover:bg-surface');
  // R12 : les epinglees (et un suivi non epingle avec des non lus), sous Notifications ; elles quittent leur groupe. L'ORDRE est celui de
  // Canaux (ecart M) : par type (Lia, canal, direct, fiche) puis ordre d'epinglage, par la
  // fonction partagee du socle (`ordreDesEpinglees`, @taki/ui).
  const epinglees = ordreDesEpinglees(
    conversations.filter(figureDansLesEpinglees),
    (conversation) => (conversation.source === 'lia' ? 'lia' : conversation.source === 'canaux' ? 'canal' : conversation.source === 'directs' ? 'direct' : 'fiche'),
    (conversation) => conversation.ref ?? '',
    ordreEpingles,
  );
  const ligneDe = (conversation: ConversationResume) => <LigneDeConversation key={`${conversation.source}:${conversation.id}`} conversation={conversation}
    active={conversation.id === conversationActive && source === conversation.source} classe={ligne}
    onOuvrir={() => onConversation(conversation.source, conversation.id)} onEpingler={onEpingler} onRenommer={onRenommer} onSupprimer={onSupprimer} onSourdine={onSourdine} onReglagesExternes={onReglagesExternes} sourdine={sourdines.has(conversation.ref ?? '')} />;
  return <div className="flex w-60 shrink-0 flex-col overflow-hidden bg-surface-1 px-2 pb-2 pt-2" style={largeur === undefined ? undefined : { width: largeur }} role="navigation" aria-label="Conversations">
    <div className="min-h-0 flex-1 overflow-y-auto">
      <button type="button" onClick={() => onSource('notifications')} aria-current={source === 'notifications' ? 'true' : undefined}
        className={ligne(source === 'notifications')}>
        <IconBellNotification size={14} className="shrink-0 text-ink-500" /><span className={cn("min-w-0 flex-1 truncate", badgeDeSource(conversations, 'notifications').nombre > 0 && 'font-bold')}>Notifications</span>
        <Pastille nombre={badgeDeSource(conversations, 'notifications').nombre} />
      </button>
      {epinglees.length > 0 && <section aria-label="Épinglées" className="mt-2">
        <div className="flex h-6 items-center pl-2"><span className="min-w-0 flex-1 truncate text-[0.625rem] font-semibold uppercase tracking-[0.06em] text-ink-400">Épinglées</span></div>
        {epinglees.map(ligneDe)}
      </section>}
      {groupesDuRail().map((groupe) => {
        const siennes = conversations.filter((conversation) => conversation.source === groupe.id && !conversation.archivee && !conversation.epinglee);
        const inerte = groupe.etat !== 'prete' || (groupe.id !== 'lia' && raisonSansPort !== null);
        return <section key={groupe.id} aria-label={groupe.libelle} className="mt-2">
          <div className="flex h-6 items-center gap-1 pl-2 pr-0.5">
            <span className="min-w-0 flex-1 truncate text-[0.625rem] font-semibold uppercase tracking-[0.06em] text-ink-400">{groupe.libelle}</span>
            <button type="button" aria-label={groupe.ajouterLabel ?? `Ajouter : ${groupe.libelle}`} title={inerte ? (raisonSansPort ?? groupe.raison) : groupe.ajouterLabel}
              aria-disabled={inerte || undefined} onClick={() => { if (!inerte) onAjouter(groupe.id); }}
              className={cn('flex h-5 w-5 items-center justify-center rounded-[0.3125rem] text-ink-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
                inerte ? 'cursor-not-allowed opacity-60' : 'hover:bg-surface hover:text-ink-900')}><IconPlus size={11} /></button>
          </div>
          {/* Une ligne VIDE (« Aucun sujet ») n'est jamais la conversation ouverte : jamais surlignee (passe 2, ecart N). */}
          {siennes.length === 0 ? <button type="button" onClick={() => onSource(groupe.id)}
            className={cn(ligne(false), 'text-ink-500')}>
            <span className="w-3.5 shrink-0 text-center text-ink-400"><IconeDeSource id={groupe.id} size={13} /></span>
            <span className="min-w-0 flex-1 truncate">{inerte ? 'Non branché' : groupe.id === 'lia' ? 'Aucun sujet' : groupe.id === 'canaux' ? 'Aucun canal' : 'Aucun message direct'}</span>
          </button> : siennes.map(ligneDe)}
        </section>;
      })}
    </div>
  </div>;
}

/**
 * UNE LIGNE DE CONVERSATION (R4, R12 du soir) : un clic l'ouvre ; un
 * double-clic la renomme EN PLACE si on en a le droit (Entree enregistre,
 * Echap annule) ; au survol, l'epingle et le menu « ... » (Epingler,
 * Renommer, Supprimer). Un geste sans droit est absent du menu.
 */
function LigneDeConversation({ conversation, active, classe, onOuvrir, onEpingler, onRenommer, onSupprimer, onSourdine, onReglagesExternes, sourdine }: {
  conversation: ConversationResume; active: boolean; classe: (active: boolean) => string;
  onSourdine: RailDuCentreProps['onSourdine']; onReglagesExternes: RailDuCentreProps['onReglagesExternes']; sourdine: boolean;
  onOuvrir: () => void; onEpingler: RailDuCentreProps['onEpingler']; onRenommer: RailDuCentreProps['onRenommer']; onSupprimer: RailDuCentreProps['onSupprimer'];
}): JSX.Element {
  const [edition, setEdition] = useState<string | null>(null);
  const [menu, setMenu] = useState(false);
  // Un brouillon attend dans cette conversation (ou dans l'un de ses fils) : jamais sur la ligne ouverte,
  // dont le texte est deja sous les yeux (la marque clignoterait a la frappe).
  const clesDeBrouillons = useClesDeBrouillons();
  const cleDeBrouillon = conversation.source === 'lia' ? cleDeSujetLia(conversation.id)
    : conversation.source === 'notifications' ? null : cleDeConversation(conversation.id);
  const brouillon = !active && cleDeBrouillon !== null && aUnBrouillon(clesDeBrouillons, cleDeBrouillon);
  const champ = useRef<HTMLInputElement>(null);
  const racine = useRef<HTMLDivElement>(null);
  useEffect(() => { if (edition !== null) requestAnimationFrame(() => { champ.current?.focus(); champ.current?.select(); }); }, [edition !== null]);
  useEffect(() => {
    if (!menu) return;
    const fermer = (e: MouseEvent | KeyboardEvent): void => {
      // Echap ferme le menu, et le dit (preventDefault) : la fenetre, elle, reste ouverte.
      if (e instanceof KeyboardEvent) { if (e.key === 'Escape') { e.preventDefault(); setMenu(false); } return; }
      if (!racine.current?.contains(e.target as Node)) setMenu(false);
    };
    // Echap en CAPTURE sur `window` : le menu se ferme avant que l'app ne voie la touche (marquee traitee).
    document.addEventListener('mousedown', fermer);
    window.addEventListener('keydown', fermer, true);
    return () => { document.removeEventListener('mousedown', fermer); window.removeEventListener('keydown', fermer, true); };
  }, [menu]);
  const renommable = conversation.renommable === true && onRenommer !== undefined;
  const supprimable = conversation.supprimable === true && onSupprimer !== undefined;
  const epinglable = conversation.ref !== undefined && onEpingler !== undefined;
  const valider = (): void => {
    const titre = edition?.trim() ?? '';
    setEdition(null);
    if (titre !== '' && titre !== conversation.titre) void onRenommer?.(conversation, titre);
  };
  const glyphe = conversation.glyphe === 'hash' ? <IconSignHashtag size={12} /> : conversation.glyphe === 'cadenas' ? <IconPadlockSquare1 size={12} aria-label="Canal privé" />
    : conversation.glyphe === 'presence' ? (conversation.personne
      // P22 : la photo de la personne ; V3-2 : sans photo, le MEME rond que le fil (Avatar, initiale lisible,
      // cercle marque d'un filet pour rester visible sur la ligne active). L'en-tete du direct ouvert garde son icone.
      ? <Avatar size={20} src={conversation.personne.photoURL} initials={conversation.personne.initiales} className="bg-surface-3 text-ink-700 ring-1 ring-inset ring-border" />
      : <IconUserFaceMale size={13} />) : <IconeDeSource id={conversation.source} size={13} />;
  if (edition !== null) return <div className={cn(classe(true), 'cursor-text')}>
    <span className="flex w-5 shrink-0 items-center justify-center text-ink-400">{glyphe}</span>
    <input ref={champ} value={edition} maxLength={80} aria-label={`Renommer ${conversation.titre}`} onChange={(event) => setEdition(event.target.value)}
      onBlur={valider} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); valider(); } if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setEdition(null); } }}
      className="min-w-0 flex-1 rounded-[0.25rem] bg-surface px-1 text-[0.8125rem] font-medium text-ink-900 outline-none ring-1 ring-accent-border" />
  </div>;
  const bouton = 'flex h-5 w-5 shrink-0 items-center justify-center rounded-[0.3125rem] text-ink-400 hover:bg-surface-3 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border';
  return <div ref={racine} className="group/ligne relative">
    <button type="button" onClick={onOuvrir} onDoubleClick={() => { if (renommable) setEdition(conversation.titre); }}
      aria-current={active ? 'true' : undefined} title={renommable ? `${conversation.titre} (double-clic : renommer)` : conversation.titre}
      className={cn(classe(active), 'group-hover/ligne:pr-12 group-focus-within/ligne:pr-12', menu && 'pr-12')}>
      <span className="flex w-5 shrink-0 items-center justify-center text-ink-400">{glyphe}</span>
      <span className="min-w-0 flex-1 truncate">{conversation.titre}{conversation.partagePar && <span className="ml-1 text-[0.6875rem] font-normal text-ink-400">partagé par {conversation.partagePar}</span>}</span>
      {brouillon && <span role="img" aria-label="Brouillon" title="Brouillon" className="flex shrink-0 items-center text-ink-400"><IconPen1 size={11} /></span>}
      {conversation.enCours ? <span role="img" aria-label="Lia répond" title="Lia répond" className="h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-border border-t-ink-500 group-hover/ligne:invisible motion-reduce:animate-none" />
        : conversation.nonLus > 0 ? <Pastille nombre={conversation.nonLus} className="group-hover/ligne:invisible" />
        : conversation.pointLime ? <span aria-label="Réponse prête" className="h-[0.4375rem] w-[0.4375rem] rounded-pill bg-accent-dot group-hover/ligne:invisible" /> : null}
    </button>
    <span className={cn('absolute inset-y-0 right-1 flex items-center gap-0.5', menu ? 'flex' : 'hidden group-hover/ligne:flex group-focus-within/ligne:flex')}>
      {epinglable && <button type="button" onClick={() => onEpingler?.(conversation)} aria-label={conversation.epinglee ? `Désépingler ${conversation.titre}` : `Épingler ${conversation.titre}`}
        title={conversation.epinglee ? 'Désépingler' : 'Épingler'} aria-pressed={conversation.epinglee === true} className={cn(bouton, conversation.epinglee && 'text-ink-900')}><IconeEpingle pleine={conversation.epinglee} size={11} /></button>}
      {(epinglable || renommable || supprimable || onSourdine) && <button type="button" onClick={() => setMenu((v) => !v)} aria-label={`Actions de ${conversation.titre}`} aria-haspopup="menu" aria-expanded={menu}
        title="Actions" className={bouton}><IconFullSpaced size={11} /></button>}
    </span>
    {menu && <ul role="menu" aria-label={`Actions de ${conversation.titre}`} className="absolute right-1 top-full z-20 mt-0.5 w-44 rounded-md border border-border bg-surface p-1 text-ink-900 shadow-pop">
      {epinglable && <li role="none"><button type="button" role="menuitem" onClick={() => { setMenu(false); onEpingler?.(conversation); }} className="flex h-8 w-full items-center gap-2 rounded-sm px-2 text-left text-[0.8125rem] hover:bg-surface-3">
        <IconeEpingle pleine={conversation.epinglee} size={12} />{conversation.epinglee ? 'Désépingler' : 'Épingler'}</button></li>}
      {onSourdine && conversation.ref && <li role="none"><button type="button" role="menuitem" onClick={() => { setMenu(false); onSourdine(conversation); }} className="flex h-8 w-full items-center rounded-sm px-2 text-left text-[0.8125rem] hover:bg-surface-3">{sourdine ? 'Réactiver' : 'Mettre en sourdine'}</button></li>}
      {onReglagesExternes && conversation.ref && <li role="none"><button type="button" role="menuitem" onClick={() => { setMenu(false); onReglagesExternes(conversation); }} className="flex min-h-8 w-full items-center rounded-sm px-2 text-left text-[0.8125rem] hover:bg-surface-3">Notifications externes</button></li>}
      {renommable && <li role="none"><button type="button" role="menuitem" onClick={() => { setMenu(false); setEdition(conversation.titre); }} className="flex h-8 w-full items-center gap-2 rounded-sm px-2 text-left text-[0.8125rem] hover:bg-surface-3">Renommer</button></li>}
      {supprimable && <li role="none"><button type="button" role="menuitem" onClick={() => { setMenu(false); onSupprimer?.(conversation); }} className="flex h-8 w-full items-center gap-2 rounded-sm px-2 text-left text-[0.8125rem] text-danger-ink hover:bg-surface-3">Supprimer</button></li>}
    </ul>}
  </div>;
}

/** Une icone pour la rangee d'en-tete ou les emplacements : le groupe, sa taille, rien d'autre. */
export function GlypheConversation({ children }: { children: ReactNode }): JSX.Element {
  return <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm bg-surface-3 text-ink-700">{children}</span>;
}

export { IconChatTwoBubblesOval as IconCentre };
